import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { fetchICTData, deleteICTData } from "../../../store/uiSlice";
import { fetchDomains } from "../../../store/domainsSlice";  // Import domains data
import { fetchVendors } from "../../../store/vendorsSlice";
import { fetchAllStudyModes } from "../../../store/studyModeSlice";
import { fetchHoursByICTId } from "../../../store/hoursSlice";
import { fetchIctCostByIctId } from "../../../store/ictCostSlice";
import UpdateICTData from "./subComponents/ICTData/UpdateICTData";
import DeleteICTData from "./subComponents/ICTData/DeleteICTData";
import { patchIctDataStatus } from "../../../store/uiSlice";
import { fetchTrainersByIctId } from '../../../store/ictTrainersByIctIdSlice';
import { fetchEmployees } from '../../../store/empDataSlice';
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "../../../Styles/Common.css";
const user = JSON.parse(localStorage.getItem("user"));
const userRole = user?.role || null;
const isReadOnly = userRole === "sales";

const ICTData = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Slices
  const { ictData = [], loading, error } = useSelector((state) => state.ui);
  const { domains = [] } = useSelector((state) => state.domains);
  const { vendors = [] } = useSelector((state) => state.vendors || {});
  const { studyModes = [] } = useSelector((state) => state.studyModes || {});
  const { hoursByIct = {}, loading: hoursLoading } = useSelector((state) => state.hours);
  const { ictCosts = [] } = useSelector((state) => state.ictCost || {});
  const trainers = useSelector((state) => state.ictTrainersByIctId.data || []);
  const employees = useSelector((state) => state.empData.employees || []);

  const [readMoreStates, setReadMoreStates] = useState({});
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // For details popup
  const [currentDetailsRecord, setCurrentDetailsRecord] = useState(null);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);

  const itemsPerPage = 10;

  // Column mapping
  const columnMapping = {
    ictName: "ICT Name",
    ictDesc: "Description",
    ictSDesc: "Short Description",
    ictType: "Course Type",
    ictImg: "Image",
    domainId: "Domain",
    vendorId: "Vendor",
    id: "Id",
  };

  const sanitizeUrl = (url) => url.replace(/([^:])\/\//g, "$1/");

  useEffect(() => {
    // Fetch main data
    dispatch(fetchICTData());
    dispatch(fetchDomains());
    dispatch(fetchVendors());
    // We might as well fetch all study modes in advance, 
    // so we have them ready for the popup
    dispatch(fetchAllStudyModes());
    dispatch(fetchEmployees());
  }, [dispatch]);

  // Determine visible columns
  useEffect(() => {
    if (ictData.length > 0) {
      const keys = Object.keys(ictData[0]).filter(
        (key) => !["created", "status", "updated"].includes(key)
      );
      setVisibleColumns(keys);
    }
  }, [ictData]);

  const toggleReadMore = (index) => {
    setReadMoreStates((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const truncateText = (text, wordLimit) => {
    const words = text.split(" ");
    return words.length > wordLimit
      ? words.slice(0, wordLimit).join(" ") + "..."
      : text;
  };

  const handleUpdate = (id) => {
    const record = ictData.find((item) => item.id === id);
    setCurrentRecord(record);
    setShowUpdatePopup(true);
  };

  const handleDelete = async (id) => {
      if (!window.confirm("Are you sure you want to delete this record?")) return;
    
      try {
        await dispatch(deleteICTData(id)).unwrap();   // ✅ use the slice
        toast.success("Record deleted successfully!");
      } catch (err) {
        toast.error(err || "Failed to delete the record.");
      }
    };
  // New handler to toggle status
    const handleStatusToggle = async (item) => {
      const newStatus = item.status === "A" ? "D" : "A";
      try {
        await dispatch(patchIctDataStatus({ id: item.id, status: newStatus })).unwrap();
        toast.success(`Status set to ${newStatus === "A" ? "Active" : "Deactivated"}`);
      } catch {
        toast.error("Failed to update status");
      }
    };
  // Show the details popup & fetch relevant data
  const handleViewDetails = (record) => {
    setCurrentDetailsRecord(record);
    setShowDetailsPopup(true);
    dispatch(fetchHoursByICTId(record.id));
    dispatch(fetchIctCostByIctId(record.id));
    dispatch(fetchTrainersByIctId(record.id));
  };

  const getEmpName = (empId) => {
    // your employee objects might use `id` or `empId` as their key
    const e = employees.find((x) => x.empId === empId || x.id === empId);
    if (!e) return empId;
    // some APIs call them empFName/empLName, others firstName/lastName
    const first = e.empFName ?? e.firstName ?? "";
    const last  = e.empLName ?? e.lastName   ?? "";
    return (first || last) ? `${first} ${last}` : empId;
  };

  const filteredData = ictData.filter((item) =>
    item.ictName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const currentPageData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return <div className="loading">Loading data...</div>;
  }

  if (error) {
    toast.error(error);
  }

  return (
    <div>
      <ToastContainer position="bottom-right" autoClose={3000} />
      <div className="header-container">
        <h2>Internship Course Training Data</h2>
        {!isReadOnly && (
        <Link to="/ict-data/insert" className="insert-link">
          +
        </Link>
        )}
      </div>

      <div className="controls">
        <div className="column-options">
          {Object.keys(columnMapping).map((column) => (
            <label key={column} className="column-checkbox">
              <input
                type="checkbox"
                checked={visibleColumns.includes(column)}
                onChange={() =>
                  setVisibleColumns((prevColumns) =>
                    prevColumns.includes(column)
                      ? prevColumns.filter((col) => col !== column)
                      : [...prevColumns, column]
                  )
                }
              />
              {columnMapping[column]}
            </label>
          ))}
        </div>

        <div className="search-section">
          <input
            type="text"
            placeholder="Search by ICT Name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input largeSearch"
          />
        </div>
      </div>

      {currentPageData.length > 0 ? (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                {Object.keys(columnMapping)
                  .filter((column) => visibleColumns.includes(column))
                  .map((key) => (
                    <th key={key}>{columnMapping[key]}</th>
                  ))}
                <th>Status</th>
                {!isReadOnly && (
                <th>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((item, index) => (
                <tr key={item.id}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  {Object.entries(item)
                    .filter(([key]) => visibleColumns.includes(key))
                    .map(([key, value], i) => (
                      <td key={i}>
                        {key === "ictName" ? (
                          // Clickable text to open details popup
                          <b
                            onClick={() => handleViewDetails(item)}
                            style={{ cursor: "pointer", color: "blue" }}
                          >
                            {value}
                          </b>
                        ) : key === "ictDesc" || key === "ictSDesc" ? (
                          <span>
                            {readMoreStates[index]
                              ? value
                              : truncateText(value, 16)}
                            {value.split(" ").length > 16 && (
                              <button
                                className="read-more-btn"
                                onClick={() => toggleReadMore(index)}
                              >
                                {readMoreStates[index]
                                  ? "Read Less"
                                  : "Read More..."}
                              </button>
                            )}
                          </span>
                        ) : key === "ictImg" ? (
                          <img
                            src={sanitizeUrl(`https://iteyonikservicesinstitute.com/${value}`)}
                            alt="ICT"
                            className="ict-img"
                            width="80"
                            height="50"
                          />
                        ) : key === "domainId" ? (
                          domains.find((d) => d.id === value)?.domain ||
                          "Unknown Domain"
                        ) : key === "vendorId" ? (
                          vendors.find((v) => v.id === value)?.vendor || 
                          "Unknown Vendor"
                        ) : (
                          value
                        )}
                      </td>
                    ))}
                      <td style={{ textAlign: "center" }}>
                        {isReadOnly ? (
                          <span>{item.status === "A" ? "Active" : "Inactive"}</span>
                        ) : (
                        <label className="switch">
                        <input
                          type="checkbox"
                          checked={item.status === "A"}
                          onChange={() => handleStatusToggle(item)}
                        />
                        <span className="slider round" />
                        </label>
                        )}
                      </td>
                      {!isReadOnly && (
                      <td>
                        <div className="actions-column">
                            <button
                              className="icon-btn update-btn"
                              onClick={() => handleUpdate(item.id)}
                            >
                              <i className="fa fa-edit" aria-hidden="true"></i>
                            </button>
                            <button
                              className="icon-btn delete-btn"
                              onClick={() => handleDelete(item.id)}
                            >
                              <i className="fa fa-trash" aria-hidden="true"></i>
                            </button>
                            <button
                              className="icon-btn open-btn"
                              onClick={() => navigate(`/ict-catalog/${item.id}`)}
                            >
                              <i className="fas fa-eye" aria-hidden="true"></i>
                            </button>
                        </div>
                      </td>
                      )}
                </tr>
              ))}
            </tbody>
          </table>

          <div className="pagination">
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index}
                className={`pagination-btn ${
                  currentPage === index + 1 ? "active" : ""
                }`}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </>
      ) : (
        <p>No data available or matching the search query.</p>
      )}

      {showUpdatePopup && !isReadOnly &&(
        <UpdateICTData
          record={currentRecord}
          onClose={() => setShowUpdatePopup(false)}
        />
      )}
      {showDeletePopup && !isReadOnly &&(
        <DeleteICTData
          record={currentRecord}
          onClose={() => setShowDeletePopup(false)}
        />
      )}

      {/* Details Popup */}
      {showDetailsPopup && currentDetailsRecord && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{currentDetailsRecord.ictName} Details</h3>

            {/* studyModes, hours, ictCosts might be undefined, so use "|| []" */}
            <div className="ictDataDetailsPop">
              <h4>Study Modes</h4>
              {studyModes.filter((mode) => mode.ictId_id === currentDetailsRecord.id).length === 0 ? (
                <p>No study modes available.</p>
              ) : (
                studyModes
                  .filter((mode) => mode.ictId_id === currentDetailsRecord.id)
                  .map((mode) => (
                    <p key={mode.id}>
                      {mode.studyMode} <i className="fa fa-check-square"></i>{" "}
                    </p>
                  ))
              )}
            </div>

            <div className="ictDataDetailsPop">
              <h4>Hours</h4>
              {!(hoursByIct[currentDetailsRecord.id]||[]).length ? (
                <p>No hours available.</p>
              ) : (
                (hoursByIct[currentDetailsRecord.id]||[]).map((hour) => (
                  <p key={hour.id}>
                    Training: {hour.TrainingHour} Hours, Practical Lab: {hour.LabHour} Hours
                    <br/>
                    Total Duration: {hour.TrainingHour + hour.LabHour} Hours
                  </p>
                ))
              )}
            </div>

            <div className="ictDataDetailsPop">
              <h4>ICT Cost</h4>
              {ictCosts.filter((cost) => cost.ictid === currentDetailsRecord.id).length === 0 ? (
                <p>No ICT cost available.</p>
              ) : (
                ictCosts
                  .filter((cost) => cost.ictid === currentDetailsRecord.id)
                  .map((cost) => (
                    <div key={cost.id}>
                      <p>MRP: ₹ <s>{cost.ictMrp}</s>, Sell Price: ₹ <strong>{cost.sellPrice}</strong></p>
                    </div>
                  ))
              )}
            </div>

            {/* — TRAINERS — */}
            <div className="ictDataDetailsPop">
              <h4>Trainers</h4>
              {!trainers.length ? (
                <p>No trainers assigned.</p>
              ) : (
                trainers.map((tr) => (
                  <li key={tr.id}>{getEmpName(tr.empId)}</li>
                ))
              )}
            </div>

            <button
              className="close-btn fa fa-close"
              onClick={() => setShowDetailsPopup(false)}
            ></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ICTData;
